import { inject, Injectable } from '@angular/core';
import { IDecaCredits, IDecaDocument, IDecaDocumentCompany } from '@okcargo-apps/shared-domain';
import {
  FindAvailableCompanyCreditsQueryHandler,
  FindDigitalControlDocQueryHandler,
  SearchDigitalControlDocCompaniesQueryHandler,
  SearchDigitalControlDocsForCarrierQueryHandler,
  SearchDigitalControlDocsForShipperQueryHandler,
} from '@okcargo/query-processor';
import {
  SearchOptionsInput,
  transformToSearchOptions,
  transformToSearchResult,
} from '../query-repository/query.mapper';
import { FindOptions, SearchResult } from '../query-repository/query.model';
import { BaseQueryRepository } from '../query-repository/query.repository';
import { transformToIDecaCompanyList, transformToIDecaCredits, transformToIDecaDocument } from './deca-query.mapper';

@Injectable({
  providedIn: 'root',
})
export class DecaQueryService {
  private readonly queryRepository = inject(BaseQueryRepository);

  private readonly findDecaDocQueryHandler: FindDigitalControlDocQueryHandler;
  private readonly searchDigitalControlDocsForCarrierQueryHandler: SearchDigitalControlDocsForCarrierQueryHandler;
  private readonly searchDigitalControlDocsForShipperQueryHandler: SearchDigitalControlDocsForShipperQueryHandler;
  private readonly searchDigitalControlDocCompaniesQueryHandler: SearchDigitalControlDocCompaniesQueryHandler;
  private readonly findAvailableCompanyCreditsQueryHandler: FindAvailableCompanyCreditsQueryHandler;

  constructor() {
    this.findDecaDocQueryHandler = new FindDigitalControlDocQueryHandler(this.queryRepository);
    this.searchDigitalControlDocsForCarrierQueryHandler = new SearchDigitalControlDocsForCarrierQueryHandler(
      this.queryRepository,
    );
    this.searchDigitalControlDocsForShipperQueryHandler = new SearchDigitalControlDocsForShipperQueryHandler(
      this.queryRepository,
    );
    this.searchDigitalControlDocCompaniesQueryHandler = new SearchDigitalControlDocCompaniesQueryHandler(
      this.queryRepository,
    );
    this.findAvailableCompanyCreditsQueryHandler = new FindAvailableCompanyCreditsQueryHandler(this.queryRepository);
  }

  public async findDeca(options: FindOptions): Promise<IDecaDocument> {
    const response = await this.findDecaDocQueryHandler.handle(options);
    return transformToIDecaDocument(response);
  }

  public async searchDecaAsShipper(options: SearchOptionsInput = {}): Promise<SearchResult<IDecaDocument>> {
    const response = await this.searchDigitalControlDocsForShipperQueryHandler.handle(
      transformToSearchOptions(options),
    );
    return transformToSearchResult(response, transformToIDecaDocument);
  }

  public async searchDecaAsCarrier(options: SearchOptionsInput = {}): Promise<SearchResult<IDecaDocument>> {
    const response = await this.searchDigitalControlDocsForCarrierQueryHandler.handle(
      transformToSearchOptions(options),
    );
    return transformToSearchResult(response, transformToIDecaDocument);
  }

  public async searchDecaCompanies(options: SearchOptionsInput = {}): Promise<IDecaDocumentCompany[]> {
    const response = await this.searchDigitalControlDocCompaniesQueryHandler.handle(transformToSearchOptions(options));
    return transformToIDecaCompanyList(response);
  }

  public async findAvailableCredits(options: FindOptions): Promise<IDecaCredits> {
    const query = {
      companyId: options.id,
      abortSignal: options.abortSignal,
    };
    const response = await this.findAvailableCompanyCreditsQueryHandler.handle(query);
    return transformToIDecaCredits(response);
  }
}
